/**
 * PATH: packages/ui/src/kanban/audit-revert/planForFieldEntry.ts
 * FILE_ID: UI-KANBAN-AUDIT-REVERT-001-FIELDS
 * PURPOSE: Map the reversible single-field audit events (status, priority,
 *          due/start/action dates + note, blocked reason, sensitive, snooze,
 *          pinned) → a revert plan whose inverse is `updateCard(id, {field:
 *          before})`. Returns `null` for any other event so the orchestrator
 *          can fall through to its own cases / not-reversible fallback.
 * WHY: Lifted verbatim out of the former 540-line auditRevert.ts single switch
 *      (refactor-kanban-decomp3). All ten cases follow the same
 *      capture-`before` → `updateCard` pattern; grouping them keeps the
 *      orchestrator focused on the move/reorder/assignees cases.
 * DEPENDENCIES:
 *   - ../api: KanbanApi (only `updateCard` used here)
 *   - ../types: AuditLogEntry, Card
 *   - ./types: CardAuditRevertPlan
 *   - ./readers: the before-value narrowing readers + _MISSING sentinel
 */
import type { KanbanApi } from '../api'
import type { AuditLogEntry, Card } from '../types'
import type { CardAuditRevertPlan } from './types'
import {
  _MISSING,
  _missingBefore,
  _readBoolean,
  _readNullableString,
  _readNumber,
  _readString,
  _truncate,
} from './readers'

/**
 * Pure mapping for the single-field reversible events. Returns a plan
 * for the ten field cases, or `null` when the event isn't one of them
 * (the orchestrator then applies its own cases / fallback). No IO.
 */
export function planForFieldEntry(input: {
  entry: AuditLogEntry
  card: Pick<Card, 'id' | 'lane'>
  api: Pick<KanbanApi, 'updateCard'>
}): CardAuditRevertPlan | null {
  const { entry, card, api } = input

  switch (entry.event) {
    // ----- Reversible-field events (qb!2864) ---------------------------
    //
    // All seven of these follow the same pattern: capture `before`,
    // call `updateCard(card.id, { <field>: before })`. Wrapped in a
    // helper so the per-field cases stay one-liners and the label /
    // describe copy is the only thing each case customises.

    case 'status_changed': {
      const before = _readString(entry.before)
      if (before === null) {
        return _missingBefore('status', 'previous status')
      }
      const after = _readString(entry.after) ?? ''
      // Archive ↔ restore is the most common case — give it copy that
      // reads naturally rather than "Revert to OPEN status".
      const isArchiveAction = after === 'ARCHIVED' && before !== 'ARCHIVED'
      const isRestoreAction = before === 'ARCHIVED' && after !== 'ARCHIVED'
      const label = isArchiveAction
        ? 'Restore card'
        : isRestoreAction
        ? 'Re-archive card'
        : 'Revert status'
      const describe = isArchiveAction
        ? `Restore this card to "${before}" (undo archive).`
        : isRestoreAction
        ? `Move this card back to "${before}".`
        : `Set status back to "${before}".`
      return {
        canRevert: true,
        label,
        describe,
        execute: async () => {
          return await api.updateCard(card.id, { status: before })
        },
      }
    }

    case 'priority_changed': {
      const before = _readNumber(entry.before)
      if (before === null) {
        return _missingBefore('priority', 'previous priority')
      }
      return {
        canRevert: true,
        label: 'Revert priority',
        describe: `Set priority back to ${before}.`,
        execute: async () => {
          return await api.updateCard(card.id, { priority: before })
        },
      }
    }

    case 'due_date_changed': {
      // Nullable: a date or `null` (when before-value was "no due date").
      const before = _readNullableString(entry.before)
      // `_readNullableString` returns the *sentinel* `_MISSING` when
      // the field is absent vs. an explicit `null`. We accept null
      // (clear the due date) but reject absent.
      if (before === _MISSING) {
        return _missingBefore('due_date', 'previous due date')
      }
      return {
        canRevert: true,
        label: 'Revert due date',
        describe:
          before === null
            ? 'Clear the due date (it had none before this action).'
            : `Set due date back to ${before}.`,
        execute: async () => {
          return await api.updateCard(card.id, { due_date: before })
        },
      }
    }

    case 'start_date_changed': {
      const before = _readNullableString(entry.before)
      if (before === _MISSING) {
        return _missingBefore('start_date', 'previous start date')
      }
      return {
        canRevert: true,
        label: 'Revert start date',
        describe:
          before === null
            ? 'Clear the start date (it had none before this action).'
            : `Set start date back to ${before}.`,
        execute: async () => {
          return await api.updateCard(card.id, { start_date: before })
        },
      }
    }

    // FEAT-2026-06-29-KANBAN-ACTION-DATE — the primary "next action" date
    // + its note are reversible just like due/start (nullable string) and
    // blocked_reason (free text).
    case 'action_date_changed': {
      const before = _readNullableString(entry.before)
      if (before === _MISSING) {
        return _missingBefore('action_date', 'previous action date')
      }
      return {
        canRevert: true,
        label: 'Revert action date',
        describe:
          before === null
            ? 'Clear the action date (it had none before this action).'
            : `Set action date back to ${before}.`,
        execute: async () => {
          return await api.updateCard(card.id, { action_date: before })
        },
      }
    }

    case 'action_note_changed': {
      const before = _readNullableString(entry.before)
      if (before === _MISSING) {
        return _missingBefore('action_note', 'previous action note')
      }
      return {
        canRevert: true,
        label: 'Revert action note',
        describe:
          before === null || before === ''
            ? 'Clear the action note (it had none before this action).'
            : `Restore the previous action note: "${_truncate(before, 60)}"`,
        execute: async () => {
          return await api.updateCard(card.id, { action_note: before })
        },
      }
    }

    case 'blocked_reason_changed': {
      const before = _readNullableString(entry.before)
      if (before === _MISSING) {
        return _missingBefore('blocked_reason', 'previous blocked reason')
      }
      return {
        canRevert: true,
        label: 'Revert blocked reason',
        describe:
          before === null || before === ''
            ? 'Clear the blocked reason (it had none before this action).'
            : `Restore the previous blocked reason: "${_truncate(before, 60)}"`,
        execute: async () => {
          return await api.updateCard(card.id, { blocked_reason: before })
        },
      }
    }

    case 'sensitive_changed': {
      const before = _readBoolean(entry.before)
      if (before === null) {
        return _missingBefore('sensitive', 'previous sensitivity flag')
      }
      return {
        canRevert: true,
        label: before ? 'Re-mark sensitive' : 'Un-mark sensitive',
        describe: before
          ? 'Mark this card sensitive again.'
          : 'Remove the sensitive flag from this card.',
        execute: async () => {
          return await api.updateCard(card.id, { sensitive: before })
        },
      }
    }

    case 'snoozed_until_changed': {
      const before = _readNullableString(entry.before)
      if (before === _MISSING) {
        return _missingBefore('snoozed_until', 'previous snooze date')
      }
      return {
        canRevert: true,
        label: 'Revert snooze',
        describe:
          before === null
            ? 'Wake this card (it was not snoozed before this action).'
            : `Restore snooze until ${before}.`,
        execute: async () => {
          return await api.updateCard(card.id, { snoozed_until: before })
        },
      }
    }

    // FEAT-2026-05-28-KANBAN-CARD-PIN — same shape as sensitive_changed
    // (boolean before/after). Copy is "Re-pin" / "Unpin" so the operator
    // reads it as the inverse action they're about to take.
    case 'pinned_changed': {
      const before = _readBoolean(entry.before)
      if (before === null) {
        return _missingBefore('pinned', 'previous pin state')
      }
      return {
        canRevert: true,
        label: before ? 'Re-pin card' : 'Unpin card',
        describe: before
          ? 'Pin this card to the top of its list again.'
          : 'Remove the pin from this card.',
        execute: async () => {
          return await api.updateCard(card.id, { pinned: before })
        },
      }
    }

    default:
      return null
  }
}
